import { useCallback, useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs, { type Dayjs } from 'dayjs';
import { Body2 } from '@/components/@IntranetLibrary/Typography';
import DatePicker from '@/components/@IntranetLibrary/DatePicker';
import {
  ApprovalFormTable,
  ApprovalTableCellInput,
  approvalOutlineSelectSx,
} from '@/components/Approval/@components/ApprovalFormTable';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';
import SearchableSelect from '@/components/Approval/@components/SearchableSelect';
import { SapBudgetIncreaseRows } from '@/components/Approval/@components/SapBudgetIncrease';
import { SapBudgetIncreaseRoot } from '@/components/Approval/@components/SapBudgetIncrease/styles';
import { getSapBudgetIncreaseDraftSourceUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { MyInfoState } from '@/recoil/atom';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  findSelectedAccount,
  formatBudgetAmountInput,
  formatBudgetDepartment,
  getSapBudgetIncreaseMinMonth,
  SAP_BUDGET_INCREASE_ACCOUNT_EMPTY_MESSAGE,
  SAP_BUDGET_INCREASE_ACCOUNT_ERROR_MESSAGE,
  type SapBudgetIncreaseDraftSource,
  type SapBudgetIncreaseState,
} from '@/utils/approval/sapBudgetIncrease';
import { FieldBlock, FieldHeader } from './styles';
import type { ApprovalFormValues } from './types';

const CONTROL_HEIGHT = 44;

type SapBudgetIncreaseFieldProps = {
  /** 양식 응답이 온 뒤 true — 그때 이번 달 계정 1회 조회 */
  ready: boolean;
};

/**
 * SAP 예산증액 신청(187) — 증액 연월을 고르면 계정 목록 조회, 첫 계정 자동 선택.
 * 편집도 저장값 없이 이번 달·첫 계정·빈 금액으로 다시 시작(레거시 동일)
 */
const SapBudgetIncreaseField = ({ ready }: SapBudgetIncreaseFieldProps) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const myInfo = useAtomValue(MyInfoState);
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'sapBudgetIncrease' });
  const requestIdRef = useRef(0);
  const startedRef = useRef(false);

  useEffect(
    () => () => {
      requestIdRef.current += 1;
    },
    [],
  );

  const update = useCallback(
    (patch: Partial<SapBudgetIncreaseState>) => {
      const current = getValues('sapBudgetIncrease');
      if (current) setValue('sapBudgetIncrease', { ...current, ...patch });
    },
    [getValues, setValue],
  );

  const fetchAccounts = useCallback(
    async (base: SapBudgetIncreaseState) => {
      const requestId = ++requestIdRef.current;
      setValue('sapBudgetIncrease', { ...base, accounts: [], loading: true });
      try {
        const res = await getNestedDataFetcher<SapBudgetIncreaseDraftSource>(
          getSapBudgetIncreaseDraftSourceUrl(base.yearMonth),
        );
        if (requestId !== requestIdRef.current) return;
        const accounts = res.display?.accounts ?? [];
        const keep = accounts.some(
          (a) => (a.costElement ?? '') === base.costElement,
        );
        update({
          accounts,
          costElement: keep ? base.costElement : accounts[0]?.costElement ?? '',
          loading: false,
        });
        if (!accounts.length) {
          snackbar.showError({
            message: t(SAP_BUDGET_INCREASE_ACCOUNT_EMPTY_MESSAGE),
          });
        }
      } catch {
        if (requestId !== requestIdRef.current) return;
        update({ accounts: [], costElement: '', loading: false });
        snackbar.showError({
          message: t(SAP_BUDGET_INCREASE_ACCOUNT_ERROR_MESSAGE),
        });
      }
    },
    [setValue, update, snackbar, t],
  );

  useEffect(() => {
    if (!ready || startedRef.current) return;
    startedRef.current = true;
    fetchAccounts({
      yearMonth: dayjs().format('YYYY-MM'),
      accounts: [],
      costElement: '',
      amount: '',
    });
  }, [ready, fetchAccounts]);

  const handleMonthChange = (value: Dayjs | null) => {
    if (!state || !value?.isValid()) return;
    const yearMonth = value.format('YYYY-MM');
    if (yearMonth === state.yearMonth) return;
    // 레거시 — 연월을 바꾸면 첫 계정으로
    fetchAccounts({ ...state, yearMonth, costElement: '', userChanged: true });
  };

  const account = findSelectedAccount(state);

  return (
    <FieldBlock>
      <FieldHeader>
        <InfoSectionTitle>{t('예산증액 신청')}</InfoSectionTitle>
      </FieldHeader>
      <ControlRow>
        <DatePicker
          value={state ? dayjs(`${state.yearMonth}-01`) : null}
          onChange={handleMonthChange}
          minDate={getSapBudgetIncreaseMinMonth()}
          views={['year', 'month']}
          openTo="month"
          format="YYYY-MM"
          disabled={!state}
          sx={{ width: 160 }}
        />
        <SearchableSelect
          value={state?.costElement ?? ''}
          placeholder={t('계정 선택')}
          options={(state?.accounts ?? []).map((a) => ({
            value: a.costElement ?? '',
            label: a.accountName ?? a.costElement ?? '',
          }))}
          disabled={!state?.accounts.length}
          onChange={(costElement) => update({ costElement, userChanged: true })}
          sx={{
            ...approvalOutlineSelectSx,
            width: 250,
            height: CONTROL_HEIGHT,
          }}
        />
      </ControlRow>
      <TableRoot>
        <ApprovalFormTable>
          <SapBudgetIncreaseRows
            department={formatBudgetDepartment(myInfo)}
            yearMonth={state?.yearMonth ?? ''}
            account={state?.loading ? null : account}
            amountCell={
              <ApprovalTableCellInput
                className="align-right"
                value={state?.amount ?? ''}
                inputProps={{
                  inputMode: 'numeric',
                  'aria-label': t('증액 신청금액'),
                }}
                onChange={(e) =>
                  update({ amount: formatBudgetAmountInput(e.target.value) })
                }
              />
            }
            t={t}
          />
        </ApprovalFormTable>
      </TableRoot>
      {state?.loading && (
        <LoadingMessage>{t('데이터를 불러오는 중입니다...')}</LoadingMessage>
      )}
    </FieldBlock>
  );
};

const ControlRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
});

const TableRoot = styled(SapBudgetIncreaseRoot)({
  '& tbody td:last-of-type': { padding: 0 },
});

const LoadingMessage = styled(Body2)({
  marginTop: 16,
  textAlign: 'center',
  color: 'var(--dk-text-sub, #6B7280)',
});

export default SapBudgetIncreaseField;
